import {
  isMilestoneAnniversary,
  type Anniversary,
  type AnniversaryOccurrence,
} from "@couples/shared";

import { anniversaryIconComponentOf } from "../lib/schedule/anniversaryIcons";
import { anniversarySuffix } from "../lib/schedule/format";
import MilestoneRibbon from "./MilestoneRibbon";

interface Props {
  countdown: { anniversary: Anniversary; occurrence: AnniversaryOccurrence; days: number }[];
  /** 一番近い記念日カードに、紙吹雪演出中のグローを重ねる */
  showConfetti: boolean;
  onOpenAll: () => void;
}

/** ホーム上部の記念日カウントダウン。一番近い1件を強調カードで、残りを横スクロールで出す */
export default function AnniversaryCountdown({ countdown, showConfetti, onOpenAll }: Props) {
  if (countdown.length === 0) return null;
  const [nearest, ...rest] = countdown;
  if (nearest === undefined) return null;

  const nearestSuffix = anniversarySuffix(nearest.anniversary.kind, nearest.occurrence.count);
  const nearestIsMilestone = isMilestoneAnniversary(
    nearest.anniversary.kind,
    nearest.occurrence.count,
  );
  const NearestIcon = anniversaryIconComponentOf(nearest.anniversary.icon);

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] tracking-[0.08em] text-[var(--color-sub)] uppercase">
          次の記念日まで
        </span>
        <button
          type="button"
          onClick={onOpenAll}
          className="text-[11px] text-[var(--color-sub)] underline"
        >
          すべて見る
        </button>
      </div>

      <button
        type="button"
        onClick={onOpenAll}
        className={`relative mb-2 flex w-full items-center gap-3.5 overflow-hidden rounded-2xl bg-gradient-to-br from-white to-[var(--color-gold)]/[0.18] px-4 py-3.5 text-left shadow-sm ring-1 ring-[var(--color-gold)]/35 ${
          nearestIsMilestone ? "ring-[1.5px] ring-[var(--color-gold)]/60" : ""
        } ${showConfetti ? "confetti-glow" : ""}`}
      >
        <span
          className="pointer-events-none absolute -top-6 -right-6 size-24 rounded-full bg-[var(--color-gold)]/10"
          aria-hidden
        />
        {nearestIsMilestone && <MilestoneRibbon label={nearestSuffix} />}
        <span className="relative flex size-[52px] shrink-0 items-center justify-center rounded-full bg-[var(--color-gold)]/15">
          <NearestIcon className="size-6 text-[var(--color-plum)]" aria-hidden />
        </span>
        <span className="relative min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold">
            {nearest.anniversary.title}
          </span>
          <span className="mt-0.5 block truncate text-[10.5px] text-[var(--color-sub)]">
            ({nearestSuffix} · {nearest.occurrence.date.getMonth() + 1}月
            {nearest.occurrence.date.getDate()}日)
          </span>
        </span>
        <span className="relative flex shrink-0 items-baseline gap-0.5">
          <span
            className={`leading-none font-bold [font-variant-numeric:tabular-nums] text-[var(--color-plum)] ${
              nearestIsMilestone ? "text-[40px]" : "text-[34px]"
            }`}
          >
            {nearest.days}
          </span>
          <span className="text-[12px] text-[var(--color-sub)]">日</span>
        </span>
      </button>

      {rest.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {rest.map(({ anniversary, occurrence, days }) => {
            const suffix = anniversarySuffix(anniversary.kind, occurrence.count);
            const milestone = isMilestoneAnniversary(anniversary.kind, occurrence.count);
            return (
              <button
                key={anniversary.id}
                type="button"
                onClick={onOpenAll}
                className="relative flex h-24 w-28 shrink-0 flex-col justify-between rounded-xl border border-[var(--color-line)] bg-white px-2.5 py-2 text-left"
              >
                {milestone && (
                  <span
                    className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-[var(--color-gold)]"
                    aria-label="節目"
                  />
                )}
                <span className="line-clamp-2 text-[11px] leading-snug font-medium break-words">
                  {anniversary.title}
                </span>
                <span>
                  <span className="block truncate text-[9.5px] whitespace-nowrap text-[var(--color-sub)]">
                    {suffix} · {occurrence.date.getMonth() + 1}月{occurrence.date.getDate()}日
                  </span>
                  <span className="mt-0.5 flex items-baseline gap-0.5">
                    <span className="text-[15px] leading-none font-bold [font-variant-numeric:tabular-nums] text-[var(--color-plum)]">
                      {days}
                    </span>
                    <span className="text-[9.5px] text-[var(--color-sub)]">日</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
